import React from 'react';
import styled from 'styled-components';
import { Formik, FormikHelpers } from 'formik';
import { toaster } from 'components/v2/toast';
import {
  firstNameValidationSchema,
  lastNameValidationSchema,
  passwordValidationSchema,
} from 'constants/userValidationSchemes';
import { useTranslation } from 'react-i18next';
import AuthService from 'services/AuthService';
import { RegisterModel } from 'services/EpdClient';
import { PoorMansError } from 'util/utils';
import * as Yup from 'yup';
import AuthLayoutFrame from '../../../components/auth/AuthLayoutFrame';
import { Link, RouteComponentProps } from 'react-router-dom';
import EpdRoutes from 'routes/EpdRoutes';
import InputFormField from 'components/v2/form/InputFormField';
import { PrimaryButton } from 'components/v2/buttons';
import { StyledPrimeCheckbox } from 'components/v2/form/styled';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';
import AuthTitle from '../../../components/auth/AuthTitle';
import { HyperLink } from 'styles/Styles.styled';

type RegisterValues = RegisterModel & { acceptTerms: boolean };

const initialValues: RegisterValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  acceptTerms: false,
};

const ERROR_MARKER_FOR_EMAIL_IN_USE_JUST_TO_PAINT_BORDER_RED = '!';

const RegisterUser: React.FC<RouteComponentProps> = ({ history }) => {
  const { t } = useTranslation();

  const registerSchema = Yup.object({
    firstName: firstNameValidationSchema(t),
    lastName: lastNameValidationSchema(t),
    email: Yup.string().email('Invalid email address').required(t('account.validation.emailRequired')),
    password: passwordValidationSchema(t),
    confirmPassword: Yup.string()
      .nullable()
      .oneOf([Yup.ref('password')], t('account.validation.passwordsNotMatch'))
      .required(t('account.validation.confirmPasswordRequired')),
    acceptTerms: Yup.boolean()
      .required('The terms and conditions must be accepted.')
      .oneOf([true], 'The terms and conditions must be accepted.'),
  });

  const createUser = async (values: RegisterValues, { setErrors }: FormikHelpers<RegisterValues>) => {
    try {
      await AuthService.registerUser(values, localStorage.getItem('theme') || '');
      history.push(EpdRoutes.account.register.userSuccess);
    } catch (exception) {
      const errorCode = PoorMansError(exception);
      switch (errorCode) {
        case 'DuplicateUserName':
        case 'DuplicateEmail':
          toaster({
            severity: 'red-exclamation-error',
            summary: 'This email is already in use.',
            details: 'Please choose another email to register on the EPD Portal.',
          });
          setErrors({ email: ERROR_MARKER_FOR_EMAIL_IN_USE_JUST_TO_PAINT_BORDER_RED });
          break;
        case 'EmailFailed':
          toaster({
            severity: 'error',
            summary: 'Temporary technical difficulties',
            details: (
              <>
                User registered successfully, but the confirmation email failed. Please contact our support at{' '}
                <a href="mailto:support@environdec.com">support@environdec.com</a> to verify your email.
              </>
            ),
          });
          break;
        default:
          history.push(EpdRoutes.account.register.userError);
      }
    }
  };

  return (
    <AuthLayoutFrame>
      <BodyContainer>
        <Heading>
          <AuthTitle>Register account</AuthTitle>
          <div>
            Already have an account?{' '}
            <HyperLink as={Link} to={EpdRoutes.login}>
              Log in
            </HyperLink>
          </div>
        </Heading>

        <Formik initialValues={initialValues} validationSchema={registerSchema} onSubmit={createUser} validateOnMount>
          {({ isSubmitting, handleSubmit, values, setFieldValue, errors, isValid }) => (
            <FormWrapper onSubmit={handleSubmit}>
              <InputFormField
                label="First name"
                name="firstName"
                type="firstName"
                placeholder="Type first name"
                errorPositioning="absolute"
                required
              />
              <InputFormField
                label="Last name"
                name="lastName"
                type="lastName"
                placeholder="Type last name"
                errorPositioning="absolute"
                required
              />
              <InputFormField
                label="Email"
                name="email"
                type="email"
                placeholder="email@example.com"
                errorPositioning={
                  errors.email === ERROR_MARKER_FOR_EMAIL_IN_USE_JUST_TO_PAINT_BORDER_RED ? 'none' : 'absolute'
                }
                required
              />
              <div>
                <InputFormField
                  type="password"
                  label={t('account.password')}
                  name="password"
                  autoComplete="set-password"
                  placeholder="Type password"
                  errorPositioning="static"
                  required
                />
                <PasswordRequirements>
                  Password must be at least 6 characters and include upper and lower case letters, a number, and a special
                  symbol.
                </PasswordRequirements>
              </div>
              <InputFormField
                type="password"
                label={t('account.confirmPassword')}
                name="confirmPassword"
                placeholder="Type password"
                errorPositioning="absolute"
                required
              />

              <TermsInputGroup style={{ marginTop: 10 }}>
                <StyledPrimeCheckbox
                  name="acceptTerms"
                  inputId="acceptTerms"
                  checked={values.acceptTerms}
                  onChange={(ev) => setFieldValue('acceptTerms', ev.target.checked)}
                  $size="small"
                />
                <label htmlFor="acceptTerms">
                  I have read and accept{' '}
                  <HyperLink href="https://www.environdec.com/general-terms" target="_blank">
                    General Terms of Use
                  </HyperLink>
                  {', '}
                  <HyperLink href="https://www.environdec.com/privacy-and-cookies-policies" target="_blank">
                    Privacy and Cookie Policies
                  </HyperLink>
                  {'.'}
                </label>
                <div style={{ marginLeft: 8, visibility: values.acceptTerms ? 'hidden' : 'visible' }}>
                  <TooltipErrorIcon content="Required" />
                </div>
              </TermsInputGroup>
              <RegisterButton type="submit" disabled={isSubmitting || !isValid}>
                {isSubmitting ? 'Registering...' : 'Register'}
              </RegisterButton>
            </FormWrapper>
          )}
        </Formik>
      </BodyContainer>
    </AuthLayoutFrame>
  );
};

const BodyContainer = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 40px;
  padding-bottom: 60px;

  ${(props) => props.theme.media.tablet} {
    padding-left: 80px;
    padding-right: 80px;
  }

  ${(props) => props.theme.media.desktop} {
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  font-size: 16px;
`;

const FormWrapper = styled.form`
  display: grid;
  gap: 30px;
  margin-top: 60px;
`;

const PasswordRequirements = styled.div`
  font-size: 12px;
  margin-top: 4px;
`;

const TermsInputGroup = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
`;

const RegisterButton = styled(PrimaryButton)`
  justify-content: center;
`;

export default RegisterUser;
